import type { TwilioBalanceState } from '../../services/twilioBalance'

/**
 * LOT #142 / E-20 — logique PURE du bandeau de solde Twilio (testée sans DOM).
 *
 * Trois questions, trois fonctions :
 *   1. `bannerVariant` — ce bandeau s'adresse-t-il à ce rôle, et sous quel ton ?
 *   2. `severityOf` / `isWorseThan` — l'état a-t-il EMPIRÉ depuis la fermeture ?
 *   3. `readDismissed` / `writeDismissed` / `clearDismissed` — mémoire de
 *      session, sous try/catch (Safari privé, quota, stockage bloqué).
 *
 * Règle produit (décision utilisateur #142, avant merge) — le solde Twilio ne
 * concerne QUE le super-admin :
 *   - WARN      → jaune, super-admin SEULEMENT (« Solde Twilio bas : X USD »)
 *   - CRITICAL  → rouge, super-admin SEULEMENT (montant + lien console)
 *   - SUSPENDED → rouge ; super-admin : montant/lien console ; autres rôles :
 *                 UN seul message, « Téléphonie indisponible — contactez
 *                 l'assistance. » (ni montant, ni lien, ni « bientôt »)
 *   - OK / UNKNOWN → rien
 * Le serveur applique la même matrice (`toPublicView`) : hors super-admin il
 * ne sert que SUSPENDED ou OK — la variante d'ici est une seconde ceinture.
 *
 * Fermable pour la SESSION (sessionStorage) ; réapparaît si l'état empire —
 * c'est la règle « fermeture par palier, pas par valeur » du design system
 * §5sexies, le palier étant ici l'état lui-même.
 */

export type BannerTone = 'warning' | 'danger'

export interface BannerVariant {
  tone: BannerTone
  /** true ⇔ le bandeau porte le montant et le lien console (super-admin). */
  detailed: boolean
}

/** Rang de gravité — miroir de `TWILIO_BALANCE_SEVERITY` côté backend. */
const SEVERITY: Record<TwilioBalanceState, number> = {
  OK: 0,
  UNKNOWN: 0,
  WARN: 1,
  CRITICAL: 2,
  SUSPENDED: 3,
}

export function severityOf(state: TwilioBalanceState | null | undefined): number {
  return state ? (SEVERITY[state] ?? 0) : 0
}

/** `state` est-il strictement plus grave que `reference` ? */
export function isWorseThan(
  state: TwilioBalanceState | null | undefined,
  reference: TwilioBalanceState | null | undefined,
): boolean {
  return severityOf(state) > severityOf(reference)
}

/**
 * Variante du bandeau pour un état et un rôle, ou `null` si rien ne doit
 * s'afficher. C'est LA décision de rendu : le parent (AppShell) ne rend pas
 * l'élément quand elle vaut `null`, pour que la zone de notification vide
 * n'existe pas (garde H9 — un élément rendu à `null` compterait quand même).
 */
export function bannerVariant(
  state: TwilioBalanceState | null | undefined,
  isSuperAdmin: boolean,
): BannerVariant | null {
  switch (state) {
    case 'WARN':
      return isSuperAdmin ? { tone: 'warning', detailed: true } : null
    case 'CRITICAL':
      return isSuperAdmin ? { tone: 'danger', detailed: true } : null
    case 'SUSPENDED':
      return { tone: 'danger', detailed: isSuperAdmin }
    default:
      return null
  }
}

/** Clé de session : l'état au moment de la fermeture. */
export const DISMISSED_STORAGE_KEY = 'twilio-balance-banner:dismissed'

const KNOWN_STATES = new Set<string>(Object.keys(SEVERITY))

function storage(): Storage | null {
  try {
    if (typeof window === 'undefined') return null
    return window.sessionStorage ?? null
  } catch {
    // L'accesseur lui-même peut jeter (stockage bloqué) : sans mémoire, le
    // bandeau reste affichable — c'est le comportement sûr.
    return null
  }
}

/** État fermé pendant cette session, ou `null`. Jamais d'exception. */
export function readDismissed(): TwilioBalanceState | null {
  try {
    const raw = storage()?.getItem(DISMISSED_STORAGE_KEY)
    return raw && KNOWN_STATES.has(raw) ? (raw as TwilioBalanceState) : null
  } catch {
    return null
  }
}

export function writeDismissed(state: TwilioBalanceState): void {
  try {
    storage()?.setItem(DISMISSED_STORAGE_KEY, state)
  } catch {
    // Quota ou stockage bloqué : la fermeture ne vaut que pour ce rendu.
  }
}

export function clearDismissed(): void {
  try {
    storage()?.removeItem(DISMISSED_STORAGE_KEY)
  } catch {
    // idem
  }
}

/**
 * Le bandeau doit-il être visible ? Il l'est quand une variante existe pour
 * ce rôle ET que l'état n'a pas été fermé à ce palier ou à un palier plus
 * grave. Un état qui EMPIRE (CRITICAL fermé → SUSPENDED) rouvre le bandeau.
 */
export function shouldShowBanner(
  state: TwilioBalanceState | null | undefined,
  isSuperAdmin: boolean,
  dismissed: TwilioBalanceState | null,
): boolean {
  if (!bannerVariant(state, isSuperAdmin)) return false
  if (!dismissed) return true
  return isWorseThan(state, dismissed)
}
